import {
    U2bColumnDefinitionExternalComponent,
    U2bColumnPipeName,
    U2bFilterSelectOptionsRelations,
    U2bTableData
} from '@core/components/layout/table/table.types';
import { BcmUserTableSettingKey } from '@shared/models/bcm-settings-user';
import { getIsActive } from '@core/functions/get-is-active';
import { Person } from '@shared/models/person';
import { FilterFieldType } from '@core/datafilter/available-filter-fields';
import { FILTER_OPERATORS_DEFAULT_SELECT, FILTER_OPERATORS_DEFAULT_STRING_ALT } from '@core/datafilter/constants';
import { DataFilterOperator } from '@core/datafilter/filter-operator';

export const personsTableData: U2bTableData = {
    tableId: BcmUserTableSettingKey.Persons,
    columnDefinitions: [
        {
            property: 'avatar',
            name: 'Avatar',
            hideNameInHeader: true,
            isActive: getIsActive(),
            isAvatar: true,
            avatarAttribute: 'image'
        },
        {
            property: 'identNumber',
            name: 'Ident.-Nr.',
            isActive: getIsActive(),
            filter: {}
        },
        {
            property: 'lastName',
            name: 'Nachname',
            isActive: getIsActive(),
            filter: {}
        },
        {
            property: 'firstName',
            name: 'Vorname',
            isActive: getIsActive(),
            filter: {}
        },
        {
            property: 'searchActiveTenantRelations',
            name: 'Beziehung',
            isActive: getIsActive(),
            externalComponent: U2bColumnDefinitionExternalComponent.TenantRelationColoredName,
            parseItem: (item: Person) => item.activeAndNextTenantRelationAssignments,
            filter: {
                property: 'activeAndNextTenantRelationAssignments',
                fieldType: FilterFieldType.AutoComplete,
                compareAttribute: ['tenantRelation.id', 'id'],
                operators: [
                    DataFilterOperator.Contains,
                    DataFilterOperator.NotContains,
                    DataFilterOperator.IsEmpty
                ],
                selectOptionsRelation: U2bFilterSelectOptionsRelations.TenantRelations,
                selectOptionsFilterBy: ['name'],
                selectOptionsDisplayWith: 'name',
            }
        },
        {
            property: 'fullAddress',
            name: 'Adresse',
            isActive: getIsActive(),
            filter: {}
        },
        {
            property: 'mail',
            name: 'Email',
            isActive: getIsActive(),
            filter: {}
        },
        {
            property: 'phone',
            name: 'Tel.',
            isActive: getIsActive(),
            filter: {}
        },
        {
            property: 'IBAN',
            name: 'IBAN',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'BIC',
            name: 'BIC',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'city',
            name: 'Wohnort',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'country',
            name: 'Land',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Person) => item.country?.de,
            filter: {
                property: 'country',
                fieldName: 'de',
                fieldType: FilterFieldType.AutoComplete,
                operators: FILTER_OPERATORS_DEFAULT_SELECT,
                selectOptionsRelation: U2bFilterSelectOptionsRelations.Countries,
                selectOptionsFilterBy: ['de'],
                selectOptionsDisplayWith: 'de'
            }
        },
        {
            property: 'birthDate',
            name: 'Geburtsdatum',
            isActive: getIsActive(false, false, false),
            pipe: U2bColumnPipeName.Date,
            filter: {
                fieldType: FilterFieldType.Date
            }
        },
        {
            property: 'note',
            name: 'Notizen',
            isActive: getIsActive(false, false, false),
            filter: {
                operators: FILTER_OPERATORS_DEFAULT_STRING_ALT
            }
        },
        {
            property: 'taxNumber',
            name: 'Steuer-Nr.',
            isActive: getIsActive(false, false, false),
            excludeIf: tenant => tenant.form !== 'Binnenhafen',
            filter: {},
        },
        {
            property: 'Kreditinstitut',
            name: 'Kreditinstitut',
            isActive: getIsActive(false, false, false),
            filter: {},
        },
        {
            property: 'accountHolder',
            name: 'Kontoinhaber',
            isActive: getIsActive(false, false, false),
            filter: {},
        },
        {
            property: 'SEPAMandatId',
            name: 'SEPA Mandat ID',
            isActive: getIsActive(false, false, false),
            filter: {},
        },
        {
            property: 'SEPAMandatSignDate',
            name: 'SEPA Datum',
            isActive: getIsActive(false, false, false),
            pipe: U2bColumnPipeName.Date,
            filter: {
                fieldType: FilterFieldType.Date
            }
        },
        {
            property: 'entryDate',
            name: 'Eintritt',
            isActive: getIsActive(false, false, false),
            pipe: U2bColumnPipeName.Date,
            filter: {
                fieldType: FilterFieldType.Date
            }
        },
        {
            property: 'exitDate',
            name: 'Austritt',
            isActive: getIsActive(false, false, false),
            pipe: U2bColumnPipeName.Date,
            filter: {
                fieldType: FilterFieldType.Date
            }
        },
        {
            property: 'invoicesByMail',
            name: 'Belege per Mail',
            isActive: getIsActive(false, false, false),
            parseItem: (person: Person) => person.invoicesByMail ?  '✔' : '',
            filter: {},
        },
        {
            property: 'invoicesByPost',
            name: 'Belege per Post',
            isActive: getIsActive(false, false, false),
            parseItem: (person: Person) => person.invoicesByPost ?  '✔' : '',
            filter: {},
        },
        {
            property: 'job',
            name: 'Beruf',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'companyContacts',
            name: 'Organisation',
            isActive: getIsActive(false, false, false),
            parseItem: (person: Person) => person?.companyContacts.length ?
                person?.companyContacts.map(company => company?.company.name) : '',
            filter: {
                property: 'companyContacts',
                fieldType: FilterFieldType.Text,
                compareAttribute: 'company?.company.name',
                operators: [
                    DataFilterOperator.Contains,
                    DataFilterOperator.NotContains,
                    DataFilterOperator.IsEmpty
                ],
                selectOptionsDisplayWith: 'company?.company.name',
            }
        },
        {
            property: 'salutationMail',
            name: 'Anrede E-Mail',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'invoicePaymentType',
            name: 'Rechnung Standard Zahlart',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
    ]
};
